import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";
import { StockMode } from "./types";

interface ImportStepsMaterialStockProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    mode: StockMode | null;
    onModeChange: (mode: StockMode) => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsMaterialStock = (props: ImportStepsMaterialStockProps) => {
    const { originalRowCount, validatedRows, mode, onModeChange, ...restProps } = props;
    
    // Calculate summary stats
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    
    // Count all errors
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: validRows.length,
            label: "Dòng hợp lệ",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: invalidRows.length,
            label: "Dòng không hợp lệ",
            backgroundColor: invalidRows.length > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: invalidRows.length > 0 ? "#f44336" : "#4caf50",
            textColor: invalidRows.length > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px", fontSize: "11px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "11px" }}>
                <li>Đảm bảo file Excel không có sheet ẩn, HOẶC sheet chính phải được đặt ở vị trí đầu tiên</li>
                <li>Đảm bảo dòng 1 là hướng dẫn, có thể không có hướng dẫn nhưng phải có một số văn bản (parser sẽ bỏ qua nếu là dòng trống), dòng 2 là header tiếng Việt, dòng 3 là header tiếng Anh (quan trọng nhất)</li>
                <li>Headers phải ở dòng 3</li>
                <li>Dữ liệu bắt đầu từ dòng 4</li>
                <li>Chỉ sử dụng sheet đầu tiên (sheet[0])</li>
                <li>Trường bắt buộc: material_code, name, storage, category, measurement_unit, quantity</li>
                <li>storage phải là "Kho 1" hoặc "Kho 2"</li>
                <li>quantity phải là số &gt;= 0</li>
                <li>Material với material_code phải đã tồn tại trong hệ thống</li>
                <li>Material.storage phải khớp với storage trong file</li>
            </ul>
        </>
    );

    // Convert mode to importMode format for ImportSteps component
    const importMode = mode === "add" ? "update" : mode === "set" ? "flush" : null;
    const handleImportModeChange = (newMode: "flush" | "update") => {
        if (newMode === "update") {
            onModeChange("add");
        } else {
            onModeChange("set");
        }
    };

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập tồn kho vật tư"
            summaryStats={summaryStats}
            importMode={importMode}
            onImportModeChange={handleImportModeChange}
            importModeLabels={{
                update: {
                    title: "Thêm vào tồn kho (add)",
                    description: "Số lượng trong file sẽ được cộng vào tồn kho hiện tại của vật tư.",
                },
                flush: {
                    title: "Ghi đè tồn kho (set)",
                    description: "Số lượng trong file sẽ thay thế hoàn toàn tồn kho hiện tại của vật tư. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            templateUrl="/templates/material_quantity_import.xlsx"
            previewColumns={[
                { key: "material_code", label: "Mã vật tư" },
                { key: "name", label: "Tên vật tư" },
                { key: "storage", label: "Kho" },
                { key: "category", label: "Hạng mục" },
                { key: "measurement_unit", label: "Đơn vị" },
                { key: "quantity", label: "Số lượng" },
            ]}
        />
    );
};


